import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button, Card, CardBody, CardTitle, Wordmark } from '../../ui';
import { supabase } from '../../shared/supabase/client';
import { callFunction, FunctionError } from '../../shared/functions';
import { useInstance } from '../instance/InstanceProvider';
import { useSession } from '../session/SessionProvider';
import { text } from '../../shared/text';

/**
 * Where an invitation is redeemed.
 *
 * The order of the four steps is the whole design:
 *
 *   1. **Stash the invite token first.** `verifyOtp` can and does reload state,
 *      and the URL is about to be scrubbed; if the token only existed in the
 *      address bar it would be gone before it was used.
 *   2. **Verify the one-time credential**, which creates the session.
 *   3. **Scrub the URL** with `replaceState`, so the credential is not left in
 *      the address bar, in the browser history, or in whatever the next page
 *      sends as a referrer.
 *   4. **Redeem**, which is the step that decides whose client this is. Until
 *      it succeeds the person is signed in and is nobody — no profile row, and
 *      therefore no row visible anywhere in the schema.
 *
 * A redemption that fails signs them back out. Leaving a session with no
 * profile behind is a half-state that looks, from the inside, exactly like a
 * broken account.
 */

/*
 * Every sentence this screen can show now comes from `src/shared/text`, and the
 * three that describe a failed redemption are also the `redeem-invite` function's
 * own words. `access-and-consent` requires that a failure gives no hint that
 * content exists, and `design.md` F5 fixes which failures must be
 * indistinguishable from each other:
 *
 * - an invitation **consumed by someone else** and one whose **address does not
 *   match** the session show exactly `client.welcome.unusable`, the same string,
 *   because a different message would confirm which client an invitation
 *   belongs to;
 * - an **expired invitation** may say so, because "this is old" reveals nothing
 *   about who it was for;
 * - an **expired sign-in link** is a different thing again: the link inside the
 *   invitation, not the invitation. It gets its own words and its own way out.
 *
 * `tests/rls/invite.test.ts` asserts those strings literally and asserts that
 * the server says the same, so changing one here fails there rather than quietly
 * changing what a stranger can learn.
 */

const INVITE_STASH_KEY = 'coach-platform.invite';

type State =
  | { kind: 'working' }
  | { kind: 'expired-link' }
  | { kind: 'failed'; message: string }
  | { kind: 'nothing' };

export function WelcomePage() {
  const instance = useInstance();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshProfile } = useSession();
  const [state, setState] = useState<State>({ kind: 'working' });
  const started = useRef(false);

  useEffect(() => {
    // Strict mode mounts effects twice in development; a one-time credential
    // may be verified exactly once.
    if (started.current) return;
    started.current = true;

    const tokenHash = params.get('token_hash');
    const inviteFromUrl = params.get('invite');

    // Step 1, before anything can lose it.
    if (inviteFromUrl) {
      try {
        sessionStorage.setItem(INVITE_STASH_KEY, inviteFromUrl);
      } catch {
        // A browser with storage disabled still works: the value stays in the
        // closure below for this attempt.
      }
    }

    const invite = inviteFromUrl ?? readStash();

    void (async () => {
      if (!tokenHash && !invite) {
        setState({ kind: 'nothing' });
        return;
      }

      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'magiclink',
        });

        // Step 3: scrub whatever the outcome, so a failed attempt does not
        // leave a credential in the history either.
        scrubUrl();

        if (error) {
          setState({ kind: 'expired-link' });
          return;
        }
      }

      if (!invite) {
        setState({ kind: 'nothing' });
        return;
      }

      try {
        await callFunction<{ ok: true }>('redeem-invite', { invite });
      } catch (error) {
        clearStash();
        await supabase.auth.signOut();
        setState({
          kind: 'failed',
          message: error instanceof FunctionError ? error.message : text('client.welcome.unusable'),
        });
        return;
      }

      clearStash();
      await refreshProfile();
      await navigate(`/c/${instance.slug}/today`, { replace: true });
    })();
  }, [params, navigate, refreshProfile, instance.slug]);

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-4 py-10">
      <div className="mb-6 text-center">
        <Wordmark
          name={instance.displayName}
          text={instance.wordmarkText}
          logoUrl={instance.logoUrl}
          size="lg"
        />
      </div>

      <Card>
        {state.kind === 'working' ? (
          <>
            <CardTitle>{instance.displayName}</CardTitle>
            <CardBody role="status">{text('client.welcome.working')}</CardBody>
          </>
        ) : null}

        {state.kind === 'expired-link' ? (
          <>
            <CardTitle>{text('client.welcome.expiredLink')}</CardTitle>
            <CardBody>
              {text('client.welcome.expiredLinkBody', { coach: instance.coachDisplayName })}
            </CardBody>
            <div className="mt-4">
              <Button
                onClick={() => {
                  window.location.href = `mailto:?subject=${encodeURIComponent(
                    text('client.welcome.expiredLinkAction'),
                  )}`;
                }}
              >
                {text('client.welcome.expiredLinkAction')}
              </Button>
            </div>
          </>
        ) : null}

        {state.kind === 'failed' ? (
          <>
            <CardTitle>{instance.displayName}</CardTitle>
            <CardBody role="alert">{state.message}</CardBody>
          </>
        ) : null}

        {state.kind === 'nothing' ? (
          <>
            <CardTitle>{instance.displayName}</CardTitle>
            <CardBody>{text('client.welcome.noInvitation')}</CardBody>
          </>
        ) : null}
      </Card>
    </main>
  );
}

function readStash(): string | null {
  try {
    return sessionStorage.getItem(INVITE_STASH_KEY);
  } catch {
    return null;
  }
}

function clearStash(): void {
  try {
    sessionStorage.removeItem(INVITE_STASH_KEY);
  } catch {
    // Nothing to do; the token is single-use and has just been spent.
  }
}

/** Remove the credential from the address bar, the history entry and referrers. */
function scrubUrl(): void {
  const clean = `${window.location.origin}${window.location.pathname}`;
  window.history.replaceState(null, '', clean);
}
